import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { FaUser, FaEnvelope, FaPhone, FaBuilding, FaMapMarkerAlt, FaLock, FaEye, FaEyeSlash, FaCheckCircle } from "react-icons/fa";

export default function RegisterPage() {
  const { register, pendingUser } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    city: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!form.name.trim()) return setError("Name is required");
    if (!form.email.trim()) return setError("Email is required");
    if (!form.phone.trim()) return setError("Phone number is required");
    if (!form.company.trim()) return setError("Company/Shop name is required");
    if (!form.city.trim()) return setError("City is required");
    if (form.password.length < 6) return setError("Password must be at least 6 characters");
    if (form.password !== form.confirmPassword) return setError("Passwords do not match");

    setLoading(true);
    try {
      await register({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        company: form.company.trim(),
        city: form.city.trim(),
        password: form.password,
      });
      setSuccess(true);
    } catch (err) {
      if (err.code === "auth/email-already-in-use") {
        setError("This email is already registered. Please login instead.");
      } else {
        setError(err.message || "Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-success-icon">
            <FaCheckCircle />
          </div>
          <h2 className="auth-title">Registration Successful!</h2>
          <p className="auth-subtitle" style={{ maxWidth: 400 }}>
            Your registration request has been submitted successfully. Our admin team will review and approve your account shortly.
          </p>
          <p className="auth-status-badge auth-status-pending">
            ⏳ Status: Pending Approval
          </p>
          <button className="auth-btn" onClick={() => navigate("/login")}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo-icon">
          <FaUser />
        </div>
        <h2 className="auth-title">Create Account</h2>
        <p className="auth-subtitle">
          Register as a dealer to see prices and place orders
        </p>

        {pendingUser?.status === "pending" && (
          <div className="auth-status-msg auth-status-pending mb-3">
            ⏳ <strong>Notice:</strong> You already have a pending registration for <strong>{pendingUser.email}</strong>. Our admin team will approve your request shortly.
          </div>
        )}

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-field">
            <div className="auth-input-icon"><FaUser /></div>
            <input
              type="text"
              name="name"
              placeholder="Full Name *"
              value={form.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="auth-field">
            <div className="auth-input-icon"><FaEnvelope /></div>
            <input
              type="email"
              name="email"
              placeholder="Email Address *"
              value={form.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="auth-field">
            <div className="auth-input-icon"><FaPhone /></div>
            <input
              type="tel"
              name="phone"
              placeholder="Phone Number *"
              value={form.phone}
              onChange={handleChange}
              required
            />
          </div>

          <div className="auth-field">
            <div className="auth-input-icon"><FaBuilding /></div>
            <input
              type="text"
              name="company"
              placeholder="Company / Shop Name *"
              value={form.company}
              onChange={handleChange}
              required
            />
          </div>

          <div className="auth-field">
            <div className="auth-input-icon"><FaMapMarkerAlt /></div>
            <input
              type="text"
              name="city"
              placeholder="City *"
              value={form.city}
              onChange={handleChange}
              required
            />
          </div>

          <div className="auth-field">
            <div className="auth-input-icon"><FaLock /></div>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password (min 6 chars) *"
              value={form.password}
              onChange={handleChange}
              required
            />
            <button
              type="button"
              className="auth-eye-btn"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          <div className="auth-field">
            <div className="auth-input-icon"><FaLock /></div>
            <input
              type={showPassword ? "text" : "password"}
              name="confirmPassword"
              placeholder="Confirm Password *"
              value={form.confirmPassword}
              onChange={handleChange}
              required
            />
          </div>

          <button type="submit" className="auth-btn" disabled={loading}>
            {loading ? (
              <span className="auth-spinner" />
            ) : (
              "Register"
            )}
          </button>
        </form>

        <p className="auth-footer-text">
          Already have an account?{" "}
          <Link to="/login" className="auth-link">
            Login here
          </Link>
        </p>
      </div>
    </div>
  );
}
